import ReactApexChart from "react-apexcharts";
import { withNamespaces } from "react-i18next"
import { Badge, Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { COMPLIANCE_CASE_DECISION_MAP } from "src/modules/data-protection/constants";
import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";

const getConfidenceLabel = confidence => {
    if (confidence >= 90) {
        return {
            label: "Very High",
            color: "#0ab39c"
        };
    }

    if (confidence >= 75) {
        return {
            label: "High",
            color: "#299cdb"
        };
    }

    if (confidence >= 50) {
        return {
            label: "Moderate",
            color: "#f7b84b"
        };
    }

    return {
        label: "Low",
        color: "#f06548"
    };
};

const getConfidenceColor = confidence => {
    if (confidence >= 90) {
        return "#0ab39c"; // Very High
    }

    if (confidence >= 75) {
        return "#299cdb"; // High
    }

    if (confidence >= 50) {
        return "#f7b84b"; // Moderate
    }

    return "#f06548"; // Low
};

const ComplianceDecisionAnalysis = ({ t, complianceQuery }) => {
    if (complianceQuery.isFetching) return null;

    const dateUtils = new DateUtils();

    const {
        orchestration
    } = complianceQuery.data?.data || {};

    const decision = orchestration.decision;
    const decisionSupport = orchestration.decisionSupport;

    if (!decision || !decisionSupport) return null;

    const decisionConfig =
        COMPLIANCE_CASE_DECISION_MAP[decision] ||
        COMPLIANCE_CASE_DECISION_MAP.go;

    const confidence =
        Math.round(
            (decisionSupport.confidence || 0) * 100
        );

    const confidenceInfo =
        getConfidenceLabel(confidence);

    const chartOptions = {
        chart: {
            type: "radialBar",
            sparkline: {
                enabled: true
            }
        },

        colors: [getConfidenceColor(confidence)],

        plotOptions: {
            radialBar: {
                startAngle: -135,
                endAngle: 135,

                hollow: {
                    size: "65%"
                },

                track: {
                    background: "#f3f3f9"
                },

                dataLabels: {
                    name: {
                        show: false
                    },

                    value: {
                        offsetY: 8,
                        fontSize: "28px",
                        fontWeight: 600,
                        formatter: value =>
                            `${Math.round(value)}%`
                    }
                }
            }
        },

        stroke: {
            lineCap: "round"
        }
    };

    return (
        <Card className="mb-4">
            <CardHeader>
                <h5 className="mb-0">
                    {t('Decision Analysis')}
                </h5>
            </CardHeader>

            <CardBody>
                <Row>
                    <Col lg={4}>
                        <div className="text-center">
                            <Badge
                                color={
                                    decisionConfig.color
                                }
                                className={`fs-6 px-3 py-2 mb-4 bg-${decisionConfig.color}`}
                            >
                                <i
                                    className={`${decisionConfig.icon} me-1`}
                                />
                                {t(decisionConfig.label)}
                            </Badge>

                            <ReactApexChart
                                options={
                                    chartOptions
                                }
                                series={[
                                    confidence
                                ]}
                                type="radialBar"
                                height={260}
                            />

                            <div className="mt-2">
                                <div className="fw-semibold">
                                    {t('AI Confidence')}
                                </div>

                                <Badge
                                    style={{
                                        backgroundColor: confidenceInfo.color
                                    }}
                                    color="secondary"
                                    pill
                                >
                                    {t(confidenceInfo.label)}
                                </Badge>
                            </div>
                        </div>
                    </Col>

                    <Col lg={8}>
                        <h6 className="mb-3">
                            {t('Decision Rationale')}
                        </h6>

                        <p className="text-muted mb-4">
                            {decisionSupport.rationale}
                        </p>

                        <Row className="g-3">
                            <Col md={6}>
                                <div className="border rounded p-3 h-100">
                                    <div className="text-muted small mb-1">
                                        {t('Open Blockers')}
                                    </div>

                                    <div className="fs-4 fw-semibold">
                                        {
                                            decisionSupport
                                                .unresolvedBlockers
                                                ?.length
                                        }
                                    </div>
                                </div>
                            </Col>

                            <Col md={6}>
                                <div className="border rounded p-3 h-100">
                                    <div className="text-muted small mb-1">
                                        {t('Monitoring Recommendations')}
                                    </div>

                                    <div className="fs-4 fw-semibold">
                                        {
                                            decisionSupport
                                                .monitoringRecommendations
                                                ?.length
                                        }
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {decisionSupport.storedAt && (
                            <div className="mt-4 text-muted small">
                                {t('Generated on {{date}}', {
                                    date: dateUtils.convertDateToDate(
                                        decisionSupport.storedAt,
                                        INTERNATIONAL_DATE_FORMAT
                                    )
                                })}
                            </div>
                        )}
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
}

export default withNamespaces()(
    ComplianceDecisionAnalysis
);